import { defineStore } from 'pinia';
import api from '@/services/api.service';
import {
  Service,
  ServiceCreationData,
  ServiceUpdateData,
  ServiceStatus,
} from '@/types/service.types';
import { Pagination } from '@/types/common.types';
import serviceService from '@/services/service.service';
import { getApiErrorMessage } from '@/utils/apiError';
interface FetchServicesParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  status?: ServiceStatus;
}

interface ServiceState {
  services: Service[];
  pagination: Pagination | null;
  isLoading: boolean;
  error: string | null;
}

export const useServiceStore = defineStore('service', {
  state: (): ServiceState => ({
    services: [],
    pagination: null,
    isLoading: false,
    error: null,
  }),
  getters: {
    serviceCategories: (state): string[] => {
      const categories = state.services
        .map(s => s.categoryId)
        .filter((id): id is string => id !== undefined); // Type guard to filter out undefined
      return [...new Set(categories)]; // Return unique categories
    },
  },
  actions: {
    async fetchServices(params: FetchServicesParams = {}) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.get('/services', { params });
        this.services = response.data.data.services;
        this.pagination = response.data.data.pagination;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to fetch services.');
        this.services = [];
        this.pagination = null;
      } finally {
        this.isLoading = false;
      }
    },
    async addService(serviceData: ServiceCreationData) {
      this.isLoading = true;
      this.error = null;
      try {
        const newService = await serviceService.createService(serviceData);
        await this.fetchServices({ page: this.pagination?.currentPage, limit: this.pagination?.pageSize });
        return newService;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to create service.');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async editService(id: string, serviceData: ServiceUpdateData) {
      this.isLoading = true;
      this.error = null;
      try {
        const updatedService = await api.put(`/services/${id}`, serviceData);
        const index = this.services.findIndex(s => s.id === id);
        if (index !== -1) {
          this.services[index] = updatedService.data;
        }
        return updatedService;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to update service.');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async removeService(id: string) {
      this.isLoading = true;
      this.error = null;
      try {
        await api.delete(`/services/${id}`);
        this.services = this.services.filter(s => s.id !== id);
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to delete service.');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
